"use client";

import { ReactNode } from "react";
import { FiMoreVertical } from "react-icons/fi";

import {
  Menu as ChakraMenu,
  Portal,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { Icon } from "../internal/exports";
import { asSlotComponent } from "../internal/v3-slot";
import {
  resolveTestId,
  scopedTestIds,
  type TestIdProps
} from "../utils/test-ids";

const MenuTrigger = asSlotComponent(ChakraMenu.Trigger);
const MenuPositioner = asSlotComponent(ChakraMenu.Positioner);
const MenuContent = asSlotComponent(ChakraMenu.Content);
const MenuItemSlot = asSlotComponent(ChakraMenu.Item);

export interface MenuItemProps extends HTMLChakraProps<"div"> {
  /** Required by v3/zag: keys the item in the collection for keyboard nav + typeahead. */
  value: string;
  icon?: ReactNode;
  command?: string;
  disabled?: boolean;
  children?: ReactNode;
}

export function MenuItem({
  value,
  disabled,
  icon,
  children,
  ...props
}: MenuItemProps) {
  return (
    <MenuItemSlot value={value} disabled={disabled} {...props}>
      {icon}
      {children}
    </MenuItemSlot>
  );
}

export interface MenuProps extends TestIdProps {
  children: ReactNode;
  ariaLabelVerticalDotsIcon?: string;
  menuAriaLabel?: string;
  menuButtonAriaLabel?: string;
}

function Menu(props: MenuProps) {
  const {
    ariaLabelVerticalDotsIcon,
    children,
    menuAriaLabel,
    menuButtonAriaLabel
  } = props;

  const t = scopedTestIds(resolveTestId(props));

  return (
    <ChakraMenu.Root
      positioning={{ gutter: 8, placement: "left" }}
      autoFocus={false}
    >
      <MenuTrigger
        aria-label={menuButtonAriaLabel}
        onClick={(e) => e.stopPropagation()}
        css={{
          "&:focus-visible": { boxShadow: "outline" },
          "& span": { display: "flex" }
        }}
        role="button"
        cursor="pointer"
        {...t.props("trigger")}
      >
        <Icon
          justifySelf="flex-start"
          aria-label={ariaLabelVerticalDotsIcon}
          minW={6}
          h={6}
          as={FiMoreVertical}
          data-testid="card-moreOptionIcon"
        />
      </MenuTrigger>
      <Portal>
        <MenuPositioner>
          <MenuContent
            display="inline-flex"
            flexDirection="column"
            alignItems="flex-start"
            borderColor="gray.200"
            color="gray.800"
            minW={160}
            py={2}
            px={0}
            gap={{ base: 2, xl: 1 }}
            onClick={(e) => e.stopPropagation()}
            aria-label={menuAriaLabel}
            {...t.props("content")}
            css={{
              "& [data-part='item']": {
                display: "flex",
                h: { base: 12, xl: 10 },
                gap: 2,
                py: 2.5,
                px: 3,
                alignItems: "center",
                fontSize: 14,
                fontWeight: 400,
                "&:focus-visible": { boxShadow: "outline" }
              },
              "& span": {
                mx: 0
              },
              "& svg": {
                w: 5,
                h: 5
              }
            }}
          >
            {children}
          </MenuContent>
        </MenuPositioner>
      </Portal>
    </ChakraMenu.Root>
  );
}

Menu.Item = MenuItem;
export { Menu };
